import {
  Card,
  HStack,
  VStack,
  Text,
  Heading,
  IconButton,
  MenuRoot,
  MenuTrigger,
  MenuPositioner,
  MenuContent,
  MenuItem,
  Portal,
} from '@chakra-ui/react'
import { User, Mail, Phone, Home, Building2, MoreVertical, Eye, Trash2, Star } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { OwnerWithUnits } from '../types'

interface OwnerCardProps {
  owner: OwnerWithUnits
  isSelected?: boolean
  onClick?: (id: string) => void
  onDelete?: (id: string) => void
}

export function OwnerCard({ owner, isSelected, onClick, onDelete }: OwnerCardProps) {
  const { t } = useTranslation()

  const handleClick = () => {
    onClick?.(owner.id)
  }

  // Get active unit keys
  const activeUnitKeys = owner.ownerships
    ? owner.ownerships
        .filter((o) => !o.end_date && o.units)
        .map((o) => o.units!.unit_number)
        .sort()
    : []

  // Get unique residences
  const residences = owner.ownerships
    ? Array.from(
        new Map(
          owner.ownerships
            .filter((o) => o.units?.residences)
            .map((o) => [
              o.units.residences.id,
              {
                id: o.units.residences.id,
                name: o.units.residences.name,
                residence_key: o.units.residences.residence_key,
              },
            ])
        ).values()
      )
    : []

  const getResidenceDisplay = () => {
    if (residences.length === 0) return null
    if (residences.length === 1) {
      const res = residences[0]
      return res?.residence_key ? `${res.residence_key} - ${res.name}` : res?.name
    }
    return `${residences[0]?.residence_key ? residences[0]?.residence_key + ' - ' : ''}${residences[0]?.name} +${residences.length - 1}`
  }

  const residenceDisplay = getResidenceDisplay()

  const isEmailPreferred = owner.notification_preference === 'ELECTRONIC'
  const isPhonePreferred = owner.notification_preference === 'PAPER'

  return (
    <Card.Root
      data-owner-card={owner.id}
      onClick={handleClick}
      cursor="pointer"
      borderWidth="2px"
      borderColor={isSelected ? 'brand.800' : 'transparent'}
      _hover={{
        shadow: 'md',
        transform: 'translateY(-2px)',
        borderColor: isSelected ? 'brand.900' : 'gray.200',
      }}
      transition="all 0.2s"
    >
      <Card.Header pb={2}>
        <HStack justify="space-between" align="start">
          <HStack gap={2}>
            <User size={20} />
            <Heading size="md">
              {owner.first_name} {owner.last_name}
            </Heading>
          </HStack>

          {/* Actions menu - aligned top right */}
          <div onClick={(e) => e.stopPropagation()}>
            <MenuRoot>
              <MenuTrigger asChild>
                <IconButton
                  variant="ghost"
                  size="sm"
                  aria-label={t('common.actions')}
                >
                  <MoreVertical size={16} />
                </IconButton>
              </MenuTrigger>
              <Portal>
                <MenuPositioner>
                  <MenuContent minW="180px">
                    <MenuItem
                      value="details"
                      onClick={(e) => {
                        e.stopPropagation()
                        onClick?.(owner.id)
                      }}
                    >
                      <HStack gap={2}>
                        <Eye size={16} />
                        <Text>{t('common.details')}</Text>
                      </HStack>
                    </MenuItem>
                    {onDelete && (
                      <MenuItem
                        value="delete"
                        onClick={(e) => {
                          e.stopPropagation()
                          onDelete(owner.id)
                        }}
                        color="red.600"
                        _hover={{ bg: 'red.50' }}
                      >
                        <HStack gap={2}>
                          <Trash2 size={16} />
                          <Text>{t('common.delete')}</Text>
                        </HStack>
                      </MenuItem>
                    )}
                  </MenuContent>
                </MenuPositioner>
              </Portal>
            </MenuRoot>
          </div>
        </HStack>
      </Card.Header>

      <Card.Body pt={2}>
        <VStack align="stretch" gap={3}>
          {/* Email */}
          {owner.email && (
            <HStack gap={2}>
              <Mail size={14} color="var(--chakra-colors-gray-500)" />
              <Text
                fontSize="sm"
                color="gray.600"
                overflow="hidden"
                textOverflow="ellipsis"
              >
                {owner.email}
              </Text>
              {isEmailPreferred && (
                <Star size={14} />
              )}
            </HStack>
          )}

          {/* Phone */}
          {owner.phone && (
            <HStack gap={2}>
              <Phone size={14} color="var(--chakra-colors-gray-500)" />
              <Text
                fontSize="sm"
                color="gray.600"
              >
                {owner.phone}
              </Text>
              {isPhonePreferred && (
                <Star size={14} />
              )}
            </HStack>
          )}

          {/* Residence */}
          {residenceDisplay && (
            <HStack gap={2}>
              <Building2 size={14} color="var(--chakra-colors-gray-500)" />
              <Text fontSize="sm" color="gray.600">
                {residenceDisplay}
              </Text>
            </HStack>
          )}

          {/* Unit keys */}
          {activeUnitKeys.length > 0 && (
            <HStack gap={2} flexWrap="wrap">
              <Home size={14} color="var(--chakra-colors-gray-500)" />
              <Text fontSize="sm" color="gray.600" fontWeight="medium">
                {activeUnitKeys.join(', ')}
              </Text>
            </HStack>
          )}
        </VStack>
      </Card.Body>
    </Card.Root>
  )
}
